import { useQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { ApiError } from '@/lib/api'
import { fetchReservations, type Reservation } from './warehouseApi'
import type { CardTab, CardTabContext } from './types'

/**
 * Вкладки карточки остатка (тикет #74, ТЗ §4.5).
 *
 * Карточки складских документов — приёмки, отгрузки, сборки, инвентаризации и
 * перемещения — переехали в раскладки на дизайн-системе (`ReceiptRecordView`,
 * `ShipmentRecordView`, `PickRecordView`, `StocktakeRecordView`, `TransferRecordView`):
 * действия документа там в шапке, а не на вкладке «Действия». Здесь остался остаток.
 */

/** Три состояния запроса одним компонентом: иначе их путают в каждой вкладке заново. */
function AsyncSection<T>({
  query,
  empty,
  children,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined }
  empty: string
  children: (data: T) => React.ReactNode
}) {
  const { t } = useApp()

  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) {
    return (
      <p role="alert">
        {query.error instanceof ApiError && query.error.isForbidden
          ? t('access.denied')
          : t('app.error')}
      </p>
    )
  }
  if (query.data === undefined) return <p className="muted">{empty}</p>
  return <>{children(query.data)}</>
}

/** Количество на экране; `null` — прочерк, а не ноль. */
function qty(value: number | null | undefined): string {
  if (value == null || value === undefined) return '—'
  return String(value)
}

/** Короткая форма идентификатора: полный UUID в ячейке нечитаем. */
function short(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : '—'
}

/** Момент времени; пустой означает «не наступил», и это факт, а не пробел. */
function moment(value: string | null | undefined, fallback: string): string {
  return value ?? fallback
}

/**
 * Резервы на строке остатка.
 *
 * Отвечает на вопрос «почему этого нельзя отгрузить, он же лежит». Цифра
 * «зарезервировано: 3» на него не отвечает — отвечает номер заказа, который держит товар.
 */
function ReservationsPanel({ recordId }: { recordId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['stock-reservations', recordId],
    queryFn: ({ signal }) => fetchReservations(recordId, signal),
    retry: false,
  })

  return (
    <AsyncSection query={query} empty={t('card.wh.stock.noReservations')}>
      {(rows: Reservation[]) =>
        rows.length === 0 ? (
          <p className="muted">{t('card.wh.stock.noReservations')}</p>
        ) : (
          <table className="card-table">
            <thead>
              <tr>
                <th>{t('card.wh.stock.order')}</th>
                <th>{t('card.wh.line.quantity')}</th>
                <th>{t('card.wh.status')}</th>
                <th>{t('card.wh.stock.expires')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{short(row.orderId)}</td>
                  <td>{qty(row.quantity)}</td>
                  <td>{t(`warehouse.reservation.status.${row.status}`)}</td>
                  {/* Резерв без срока не протухает сам: пустая ячейка это «держим до
                      отгрузки», а не «данных нет». */}
                  <td>{moment(row.expiresAt, t('card.wh.stock.noExpiry'))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </AsyncSection>
  )
}

/** Остаток: кто держит резервы. */
export function stockTabs(): CardTab[] {
  return [
    {
      id: 'stock-reservations',
      labelKey: 'card.tab.whReservations',
      render: (context: CardTabContext) => <ReservationsPanel recordId={context.recordId} />,
    },
  ]
}

export { ReservationsPanel }
